import React, { useState } from "react";
import { Box, Button, IconButton, Typography, Checkbox, FormControlLabel, Divider, CircularProgress } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppleIcon from "@mui/icons-material/Apple";
import GoogleIcon from "@mui/icons-material/Google";
import { mobile, isIOS, isAndroid, isMobileWebBrowser } from "src/utils/platformUtils";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { cancelStripeSubscription } from "src/api/cancelStripeSubscription";
import { showErrorToast } from "src/components/AlertToastify/AlertToastify";
import CancelDialog from "./CancelDialog";
import { isAndroidNative, isIosNative, showManageSubscriptions } from "src/services/iap";
import { Browser } from "@capacitor/browser";

interface CancelMembershipProps {
  onClose: () => void;
  onBack: () => void;
  subscriptionData?: { expiryDate: string | null; hasActiveSubscription: boolean } | null;
}

const CancelMembership = ({ onClose, onBack, subscriptionData }: CancelMembershipProps) => {
  const [confirmed, setConfirmed] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelDialogSuccess, setCancelDialogSuccess] = useState(false);
  const [cancelDialogMessage, setCancelDialogMessage] = useState<string | undefined>(undefined);
  // Optional variant/title to support neutral "info" states (e.g. iOS native flow,
  // or trying to cancel an Apple/Google subscription from web where we can't remotely cancel).
  const [cancelDialogVariant, setCancelDialogVariant] = useState<"success" | "error" | "info" | undefined>(undefined);
  const [cancelDialogTitle, setCancelDialogTitle] = useState<string | undefined>(undefined);
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const isMobile = isMobileWebBrowser() || mobile;
  // Determine platform
  const isNativeApp = mobile;
  const isNativeIOS = isNativeApp && isIOS;
  const isNativeAndroid = isNativeApp && isAndroid;
  // If not native app, it's web (desktop or mobile web)
  const isWeb = !isNativeApp;

  // Get expiry date from props (only for Stripe/Web subscriptions)
  const expiryDate = subscriptionData?.expiryDate || null;

  const handleCancelMembership = async () => {
    if (!confirmed || isCanceling) return;

    try {
      setIsCanceling(true);

      // Native store subscriptions are managed by Apple/Google.
      // Open the native manage subscriptions screen. We can NOT know if the user
      // actually cancelled inside that sheet, so we show a neutral "info" dialog
      // instead of a misleading success message.
      if (isIosNative() || isAndroidNative()) {
        await showManageSubscriptions();
        setCancelDialogVariant("info");
        setCancelDialogTitle(
          isIosNative() ? "Apple subscriptions opened" : "Google Play subscriptions opened"
        );
        setCancelDialogMessage(
          isIosNative()
            ? "We opened your Apple subscription settings. If you confirmed the cancellation there, it will be processed by Apple. Otherwise, your subscription is still active."
            : "We opened your Google Play subscription settings. If you confirmed the cancellation there, it will be processed by Google Play. Otherwise, your subscription is still active."
        );
        setShowCancelDialog(true);
        setIsCanceling(false);
        return;
      }

      // Get authentication token
      const accessAuth0Token = await getSafeAccessAuth0Token();
      if (!accessAuth0Token) {
        console.error("Failed to get access token");
        showErrorToast("Authentication error. Please log in again.");
        setIsCanceling(false);
        return;
      }

      // Call cancel subscription endpoint
      console.log("🔄 Canceling Stripe subscription...");
      const result = await cancelStripeSubscription(accessAuth0Token);
      const resultPlatform = result?.platform;

      if (result.success) {
        console.log("✅ Subscription canceled successfully:", result.message);
        // Show success dialog with backend message
        setCancelDialogVariant("success");
        setCancelDialogTitle(undefined);
        setCancelDialogMessage(result.message || "Subscription canceled successfully. It will remain active until the end of the billing period.");
        setCancelDialogSuccess(true);
        setShowCancelDialog(true);
        // Close the drawer after dialog closes (dialog auto-closes after 4 seconds)
        setTimeout(() => {
          onClose();
        }, 4500); // 4 seconds for dialog + 0.5 seconds buffer
      } else if (resultPlatform === "APPLE" || resultPlatform === "GOOGLE") {
        // Active subscription was purchased on a native store. We can't remotely
        // cancel it from the web — show an informative dialog so the user knows
        // exactly what to do on their device.
        console.log(`ℹ️ Active subscription is on ${resultPlatform}. Informing user.`);
        setCancelDialogVariant("info");
        setCancelDialogTitle(
          resultPlatform === "APPLE" ? "Cancel from Apple" : "Cancel from Google Play"
        );
        setCancelDialogMessage(result.message);
        setShowCancelDialog(true);
        setIsCanceling(false);
      } else {
        console.error("❌ Failed to cancel subscription:", result.message);
        // Show error dialog
        setCancelDialogVariant("error");
        setCancelDialogTitle(undefined);
        setCancelDialogMessage(result.message || "Failed to cancel subscription. Please try again.");
        setCancelDialogSuccess(false);
        setShowCancelDialog(true);
        setIsCanceling(false);
      }
    } catch (error) {
      console.error("❌ Error canceling subscription:", error);
      // Show error dialog
      setCancelDialogVariant("error");
      setCancelDialogTitle(undefined);
      setCancelDialogMessage("An error occurred. Please try again or contact support.");
      setCancelDialogSuccess(false);
      setShowCancelDialog(true);
      setIsCanceling(false);
    }
  };

  // Handle manage redirect to Apple/Google Play subscriptions settings
  // const handleManageMembership = async () => {
  //   try {
  //     if (isNativeIOS) {
  //       // Use StoreKit 2 native API to open iOS Settings subscriptions screen
  //       // This is the recommended way and works in sandbox/test mode
  //       try {
  //         await showManageSubscriptions();
  //       } catch (error) {
  //         console.error("Error opening native subscriptions screen:", error);
  //         // Fallback to web URL if native method fails
  //         const url = "https://apps.apple.com/account/subscriptions";
  //         if (mobile) {
  //           await Browser.open({
  //             url,
  //             windowName: "_self",
  //           });
  //         } else {
  //           window.open(url, "_blank");
  //         }
  //       }
  //     } else if (isNativeAndroid) {
  //       // For Android, use web URL (will implement native later)
  //       const url = "https://play.google.com/store/account/subscriptions";
  //       if (mobile) {
  //         await Browser.open({
  //           url,
  //           windowName: "_self",
  //         });
  //       } else {
  //         window.open(url, "_blank");
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error opening subscriptions settings:", error);
  //   }
  // };

  return (
    <>
      {/* Header - Matching ManageMembership */}
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1} pb={1}>
        <IconButton aria-label="back" onClick={onBack}>
          <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
        </IconButton>
        <Typography variant="h4" color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign={"center"}>
          {isNativeIOS ? "Manage in Apple Settings" : isNativeAndroid ? "Manage in Google Play" : "Cancel Membership"}
        </Typography>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
          </IconButton>
        </Box>
      </Box>

      {/* Divider for separation*/}
      <Divider
        sx={{
          margin: {
            xs: "auto",
            md: "0px",
          },
          width: "100%",
          bgcolor: "#000000",
          my: 0.5,
        }}
      />

      <Box
        sx={{
          px: 2,
          "@media (min-width: 1025px)": {
            maxWidth: "60%",
            margin: "0 auto",
          },
          pt: 3,
        }}
      >
        {/* WEB UI */}
        {/* {isWeb && ( */}
        {/* // {true && ( */}
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            border: "1px solid #E0E0E0",
            borderRadius: 1,
            p: 2,
          }}
        >
          {/* <Typography variant="h6" sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}`, mb: 2, fontWeight: "bold" }}>
              Cancel Membership
            </Typography> */}
          <Typography variant="body2" textAlign="left" sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}`, mb: 2, lineHeight: 1.5 }}>
            <span style={{ fontWeight: "bold" }}>Warning:</span> Canceling will result in immediate loss of premium insights, GPT tools, and exclusive content.
            {expiryDate ? <> Your access will end on {expiryDate}.</> : " No active subscription found."}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                sx={{
                  color: `${PureLightThemeCaaS.colors.alpha.black[70]}`,
                  "&.Mui-checked": {
                    color: PureLightThemeCaaS.colors.alpha.orange[100],
                  },
                  paddingTop: isMobile ? 0 : 1,
                }}
              />
            }
            label={
              <Typography sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}`, fontSize: "12px", lineHeight: 1.2, paddingTop: 0 }}>
                I understand I will lose access to these benefits.
              </Typography>
            }
            sx={{ mb: 2, alignItems: isMobile ? "flex-start" : "center", ml: 0 }}
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={onClose}
              sx={{
                bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
                color: "white",
                fontWeight: "bold",
                "&:hover": {
                  bgcolor: PureLightThemeCaaS.colors.alpha.orange[80],
                },
                borderRadius: 1,
                textTransform: "none",
                //   py: 1.5,
              }}
            >
              Keep Membership
            </Button>
            <Button
              fullWidth
              variant="outlined"
              disabled={!confirmed || isCanceling}
              onClick={handleCancelMembership}
              sx={{
                borderColor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                borderRadius: 1,
                textTransform: "none",
                //   py: 1.5,
                "&:hover": {
                  borderColor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                  bgcolor: "rgba(0, 0, 0, 0.05)",
                },
                "&.Mui-disabled": {
                  borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]}`,
                  color: `${PureLightThemeCaaS.colors.alpha.black[30]}`,
                },
              }}
              startIcon={isCanceling ? <CircularProgress size={16} sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[30]}` }} /> : null}
            >
              {isCanceling ? "Canceling..." : "Cancel Membership"}
            </Button>
          </Box>
        </Box>
        {/* )} */}

        {/* iOS Native UI */}
        {/* {isNativeIOS && (
          // {true && (
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
            }}
          >
   
            <Typography variant="body2" textAlign="left" sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}`, mb: 4, lineHeight: 1.5 }}>
              Your membership is managed through your Apple ID. To cancel or make changes, please visit your Apple subscriptions settings.
            </Typography>
            <Button
              onClick={handleManageMembership}
              fullWidth
              variant="contained"
              startIcon={<AppleIcon />}
              sx={{
                bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
                color: "white",
                fontWeight: "bold",
                "&:hover": {
                  bgcolor: PureLightThemeCaaS.colors.alpha.orange[80],
                },
                borderRadius: 1,
                textTransform: "none",
                // py: 1.5,
              }}
            >
              Manage in Apple Settings
            </Button>
          </Box>
        )} */}

        {/* Android Native UI */}
        {/* {isNativeAndroid && (
          // {true && (
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
            }}
          >
           
            <Typography variant="body2" textAlign="left" sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}`, mb: 4, lineHeight: 1.5 }}>
              Your membership is managed through Google Play. To cancel or make changes, please visit your Google Play subscriptions settings.
            </Typography>
            <Button
              onClick={handleManageMembership}
              fullWidth
              variant="contained"
              startIcon={<GoogleIcon />}
              sx={{
                bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
                color: "white",
                fontWeight: "bold",
                "&:hover": {
                  bgcolor: PureLightThemeCaaS.colors.alpha.orange[80],
                },
                borderRadius: 1,
                textTransform: "none",
                // py: 1.5,
              }}
            >
              Manage in Google Play
            </Button>
          </Box>
        )} */}
      </Box>

      {/* Cancel Dialog */}
      <CancelDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        success={cancelDialogSuccess}
        variant={cancelDialogVariant}
        title={cancelDialogTitle}
        message={cancelDialogMessage}
        // Keep a longer delay when we are asking the user to review their
        // subscription status in Apple/Google settings (needs more time to read).
        autoCloseDelay={cancelDialogVariant === "info" ? 7000 : 4000}
      />
    </>
  );
};

export default CancelMembership;
